import { isInviteLink } from '@deltachat-desktop/shared/util.js'

export type InviteLinkInfo = {
  kind: 'group' | 'channel' | 'contact'
  /** Display name straight from the link — sender-controlled, cosmetic only. */
  name: string
}

/** Mirrors core's qr.rs decode_name: `+` means space, a trailing `_` marks a
 * name that was truncated when the link was generated (shown as `…`). */
function decodeName(value: string): string | null {
  let name: string
  try {
    name = decodeURIComponent(value.replace(/\+/g, '%20'))
  } catch {
    return null
  }
  // strip control chars and bidi/zero-width overrides so a crafted name
  // can't reorder or invisibly pad the card's text
  name = name.replace(
    // eslint-disable-next-line no-control-regex
    /[\u0000-\u001f\u007f\u200b-\u200f\u202a-\u202e\u2066-\u2069]/g,
    ''
  )
  return name.endsWith('_') ? name.slice(0, -1) + '…' : name
}

/**
 * Extract what an `https://i.delta.chat/#…` invite link is for, from the link
 * alone — the fragment carries the names (`g=` group, `b=` channel,
 * `n=`/`a=` contact). Deliberately NOT core's checkQr, which would create a
 * hidden contact as a side effect just for rendering a message.
 * Returns null when the fragment is unreadable; callers then render the
 * plain link and clicking still goes through the usual checkQr flow.
 */
export function parseInviteLink(url: string): InviteLinkInfo | null {
  if (!isInviteLink(url)) {
    return null
  }
  const fragment = url.slice(url.indexOf('#') + 1)
  const [fingerprint = '', ...rest] = fragment.split('&')
  const params = new Map<string, string>()
  for (const part of rest) {
    const eq = part.indexOf('=')
    if (eq > 0) {
      params.set(part.slice(0, eq), part.slice(eq + 1))
    }
  }
  // Gate like core's check_qr, so the card never labels a payload
  // differently than the dialog that opens on click: a securejoin link
  // starts with a hex fingerprint and carries the address plus the
  // authcode (the invitenumber i=/j= is optional since securejoin v3),
  // and group/channel invites also carry the grpid (x=).
  if (
    !/^[0-9A-Fa-f]{40,}$/.test(fingerprint) ||
    params.get('a') === undefined ||
    params.get('s') === undefined
  ) {
    return null
  }
  const group = params.get('g')
  if (group !== undefined) {
    const name = params.get('x') !== undefined ? decodeName(group) : null
    return name ? { kind: 'group', name } : null
  }
  const channel = params.get('b')
  if (channel !== undefined) {
    const name = params.get('x') !== undefined ? decodeName(channel) : null
    return name ? { kind: 'channel', name } : null
  }
  const contactName = params.get('n')
  const name =
    (contactName && decodeName(contactName)) || decodeName(params.get('a')!)
  return name ? { kind: 'contact', name } : null
}
